/* External */
import {
  memo, useMemo, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import { EntityName } from '@dtvgo/backend-service/normalize';

/* Components */
import SwimlaneCarouselContainer from '../SwimlaneCarousel';
import ScrollIntoViewIfFocused from '../ScrollIntoViewIfFocused';
import UpSellBanner from '../UpSellBanner';

/* Utils */
import { useItemData } from '../VirtualizedList';
import { ScrollPosition } from '../ScrollView';
import { BannerType } from '../../utils/cardsUtils';
import { getIsCurableBannerActive } from '../../utils/functions';

function LanderRow({ index }) {
  const {
    pageId,
    sourceProvider,
    carouselsInfo,
    noFeatured,
    noTitle,
    goToSidebar,
    scrolled,
    fetchedCarousels,
    handleCarouselFetchComplete,
    updatePositionCarousel,
    updateTranslate,
    getPositionsList,
    getTranslateValuesList,
    initialSectionIndex,
    updateWasCarouselFocused,
  } = useItemData();
  const carouselVerticalPosition = useMemo(() => index + 1, [index]);
  const carouselInfo = useMemo(() => carouselsInfo.get(index), [index, carouselsInfo]);
  const [initialPosition] = useState(() => getPositionsList()[index]);
  const [initialTranslate] = useState(() => getTranslateValuesList()[index]);
  const avoidFetch = useMemo(
    () => fetchedCarousels.has(carouselInfo?.sourceCarouselId || carouselInfo?.elementId),
    [carouselInfo?.sourceCarouselId, carouselInfo?.elementId, fetchedCarousels],
  );
  const isCurableBannerActive = getIsCurableBannerActive();

  // It should be removed from the code when pages v4 is the final version.
  const entityType = isCurableBannerActive
    ? EntityName.BANNER
    : BannerType.UPSELL_BANNER;

  const handlePositionCarouselChange = useCallback((newPositionCarousel) => {
    updatePositionCarousel(index, newPositionCarousel);
  }, [index, updatePositionCarousel]);

  const handleTranslateChange = useCallback((translate) => {
    updateTranslate(index, translate);
  }, [index, updateTranslate]);

  const setInitialSectionIndex = useCallback((shouldBeCero) => {
    if (shouldBeCero) {
      sessionStorage.setItem('initialSectionIndex', 0);
      sessionStorage.setItem('offsetFromStartedPage', 0);
    } else {
      sessionStorage.setItem('initialSectionIndex', index);
    }
  }, [index]);

  const handleCarouselBecameFocused = useCallback(() => {
    setInitialSectionIndex();
    updateWasCarouselFocused();
  }, [setInitialSectionIndex, updateWasCarouselFocused]);

  const handleClickSection = useCallback((shouldBeCero = false) => {
    setInitialSectionIndex(shouldBeCero);
    sessionStorage.setItem('initialSectionIndexEpg', 0);
  }, [setInitialSectionIndex]);

  const handleFetchComplete = useCallback((id, carousel) => {
    handleCarouselFetchComplete(id, carousel, index);
  }, [handleCarouselFetchComplete, index]);

  return carouselInfo.type === entityType ? (
    <ScrollIntoViewIfFocused position={ScrollPosition.CENTER}>
      <UpSellBanner elementId={isCurableBannerActive ? carouselInfo?.elementId : undefined} />
    </ScrollIntoViewIfFocused>
  ) : (
    <SwimlaneCarouselContainer
      carouselInfo={carouselInfo}
      noFeatured={noFeatured}
      noTitle={noTitle}
      pageId={pageId}
      sourceProvider={sourceProvider}
      avoidFetch={avoidFetch}
      initialPosition={initialPosition}
      initialTranslate={initialTranslate}
      stealFocusByDefault={index === initialSectionIndex}
      scrolled={scrolled}
      onFetchComplete={handleFetchComplete}
      onNoMoreLeftElements={goToSidebar}
      onPositionCarouselChange={handlePositionCarouselChange}
      onTranslateChange={handleTranslateChange}
      onBecameFocused={handleCarouselBecameFocused}
      handleClickSection={handleClickSection}
      carouselVerticalPosition={carouselVerticalPosition}
    />
  );
}

LanderRow.propTypes = {
  index: PropTypes.number.isRequired,
};

export default memo(LanderRow);
